import React, { useEffect, useRef, useState } from 'react';
import { Crosshair, Info, Monitor, Palette, RefreshCw, Smartphone } from 'lucide-react';
import { CustomStylesheetSection } from './CustomStylesheetSection';
import { Card } from './SubscriptionAppCard';
import { Button } from './SubscriptionAppButton';
import { PortalThemePreview } from './PortalThemePreview';
import { PortalLoyaltyPageId, PortalPreviewModalId, PortalPreviewMode, PortalPreviewPageId, PortalThemeColors, PortalThemeFieldId, PortalThemeState, defaultPausedPortalThemeColors, defaultPortalThemeColors, portalLoyaltyPages, portalPreviewModals, portalPreviewModes, portalPreviewPages, portalThemeFields, portalThemeStates } from '../types/portalTheme';
interface ThemeConfigurationPanelProps {
  /** Called when the merchant saves, resets, or refreshes, so the page can confirm. */
  onNotify: (message: string) => void;
}
const zoomOptions = [50, 75, 100];
export function ThemeConfigurationPanel({
  onNotify
}: ThemeConfigurationPanelProps) {
  const [themeState, setThemeState] = useState<PortalThemeState>('active');
  const [activeColors, setActiveColors] = useState<PortalThemeColors>(defaultPortalThemeColors);
  const [pausedColors, setPausedColors] = useState<PortalThemeColors>(defaultPausedPortalThemeColors);
  const isPausedState = themeState === 'paused';
  const colors = isPausedState ? pausedColors : activeColors;
  const [primaryGradient, setPrimaryGradient] = useState('');
  const [secondaryGradient, setSecondaryGradient] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  // Preview controls
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [zoom, setZoom] = useState(100);
  const [inspectMode, setInspectMode] = useState(false);
  const [previewMode, setPreviewMode] = useState<PortalPreviewMode>('subscriber');
  const [previewPage, setPreviewPage] = useState<PortalPreviewPageId>('collection');
  const [previewLoyaltyPage, setPreviewLoyaltyPage] = useState<PortalLoyaltyPageId>('rewards');
  const [previewModal, setPreviewModal] = useState<PortalPreviewModalId>('none');
  const isLoyaltyOnly = previewMode === 'loyalty';
  const [subscriptionId, setSubscriptionId] = useState('');
  const [loadedSubscriptionId, setLoadedSubscriptionId] = useState<string | null>(null);
  // Inspect mode links a preview element back to the field that paints it.
  const [focusedField, setFocusedField] = useState<PortalThemeFieldId | null>(null);
  const [focusNonce, setFocusNonce] = useState(0);
  const fieldRefs = useRef<Partial<Record<PortalThemeFieldId, HTMLDivElement | null>>>({});
  useEffect(() => {
    if (!focusedField) return;
    fieldRefs.current[focusedField]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, [focusedField, focusNonce]);
  const updateColor = (id: PortalThemeFieldId, value: string) => {
    const apply = (previous: PortalThemeColors) => ({
      ...previous,
      [id]: value
    });
    if (isPausedState) {
      setPausedColors(apply);
    } else {
      setActiveColors(apply);
    }
  };
  const handleInspect = (field: PortalThemeFieldId) => {
    setFocusedField(field);
    setFocusNonce(nonce => nonce + 1);
  };
  const handleReset = () => {
    setActiveColors(defaultPortalThemeColors);
    setPausedColors(defaultPausedPortalThemeColors);
    setPrimaryGradient('');
    setSecondaryGradient('');
    onNotify('Theme reset to your Shopify theme colours');
  };
  const handleLoadSubscription = () => {
    const trimmed = subscriptionId.trim();
    setLoadedSubscriptionId(trimmed === '' ? null : trimmed);
  };
  return <Card padding="none" data-id="element-5887" data-mp="1toztgbpu6xxt">
      <div className="p-6 border-b border-[#E2E8F0] flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5888" data-mp="1tp9fjo7rgky8">
        <div className="flex items-center gap-3" data-id="element-5889" data-mp="1otaplw6sauvp">
          <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-5890" data-mp="22rr263rbvflr">
            <Palette className="w-5 h-5 text-[#0EA5E9]" data-id="element-5891" data-mp="1yp4b452hdypm" />
          </div>
          <div data-id="element-5892" data-mp="1yn18mlk56d40">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-5893" data-mp="1ynb3amg4t8g1">
              Theme Configuration
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-5894" data-mp="110erua276qt">
              These colours control how your members' customer dashboard is
              styled
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4" data-id="element-5895" data-mp="arv2l32yeeed">
          <Button variant="secondary" onClick={() => onNotify('Colours refreshed from your Shopify theme')} iconLeft={<RefreshCw className="w-4 h-4" data-id="element-5897" data-mp="39bbpsl5nje17" />} data-id="element-5896" data-mp="atfv6n5pjn9l">
            Refresh from theme
          </Button>
          <div className="flex items-center gap-2.5" data-id="element-5898" data-mp="3ea69rqfcv8mw">
            <span id="theme-show-preview-label" className="text-[14px] font-medium text-[#475569]" data-id="element-5899" data-mp="3j63mlmretwux">
              Show preview
            </span>
            <button type="button" role="switch" aria-checked={showPreview} aria-labelledby="theme-show-preview-label" onClick={() => setShowPreview(value => !value)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${showPreview ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-5900" data-mp="3n6f78zjrvx6e">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showPreview ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-5901" data-mp="2j12ww68f6xq9" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-6" data-id="element-5902" data-mp="24a2f3als9rhg">
        <div className={`grid grid-cols-1 gap-6 ${showPreview ? 'xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]' : ''}`} data-id="element-5903" data-mp="24ac4avz07um3">
          {/* Controls */}
          <div className={`space-y-6 ${showPreview ? 'xl:max-h-[760px] xl:overflow-y-auto xl:pr-1' : ''}`} data-id="element-5904" data-mp="1zd3mwxlojulw">
            <div className="flex items-start gap-2.5 p-3 bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg" data-id="element-5905" data-mp="1vb8k1oo3p960">
              <Info className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-5906" data-mp="3pnevmokwpryu" />
              <p className="text-[13px] text-[#0369A1] m-0" data-id="element-5907" data-mp="3plc8ccb52cxt">
                Set the colours below to match your storefront, then save.
              </p>
            </div>

            <div data-id="element-5908" data-mp="7mrhzwvp1qr6">
              <span className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-5909" data-mp="7m9np5sldmlw">
                Colours
              </span>
              <div className="mb-4" data-id="element-5910" data-mp="3ut1xlof6tg1y">
                <span className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-5911" data-mp="3uu3mavp4m125">
                  Subscription state
                </span>
                <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-white p-0.5" role="group" aria-label="Subscription state colours" data-id="element-5912" data-mp="rbtktizbh6e3">
                  {portalThemeStates.map(option => <button key={option.id} type="button" onClick={() => setThemeState(option.id)} aria-pressed={themeState === option.id} className={`px-3 h-8 rounded-md text-[13px] font-medium transition-colors ${themeState === option.id ? 'bg-[#F0F9FF] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-5913" data-mp="hdcos7liu1l6">
                      {option.label}
                    </button>)}
                </div>
                <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-5914" data-mp="2zj5cszxk49ll">
                  The portal switches to the paused palette when a member's
                  subscription is paused. Each state keeps its own set of
                  colours.
                </p>
              </div>
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${showPreview ? '' : 'xl:grid-cols-4'}`} data-id="element-5915" data-mp="38hr17bppmiua">
                {portalThemeFields.map(field => <div key={field.id} ref={element => {
                fieldRefs.current[field.id] = element;
              }} className={`rounded-lg transition-shadow ${focusedField === field.id ? 'ring-2 ring-[#0EA5E9] ring-offset-2 ring-offset-white' : ''}`} data-id="element-5916" data-mp="2fueixdql0x6s">
                    <label htmlFor={`theme-config-${field.id}`} className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-5917" data-mp="2ku989cuplepq">
                      {field.label}
                    </label>
                    <div className="flex gap-3" data-id="element-5918" data-mp="1x2bvnjjfqink">
                      <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" aria-label={`${field.label} swatch`} data-id="element-5919" data-mp="1s4k6uye37cnl">
                        <input type="color" value={colors[field.id]} onChange={e => updateColor(field.id, e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                      backgroundColor: colors[field.id]
                    }} data-id="element-5920" data-mp="1bklj01x2iu7q" />
                      </label>
                      <input id={`theme-config-${field.id}`} type="text" value={colors[field.id]} onChange={e => updateColor(field.id, e.target.value)} className="flex-1 h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-5921" data-mp="1qhkyeby5vrcr" />
                    </div>
                    <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-5922" data-mp="24bvj8i71f1dj">
                      {field.help}
                    </p>
                  </div>)}
              </div>
            </div>

            <div data-id="element-5923" data-mp="2e642sge3bjzk">
              <span className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-5924" data-mp="2kv6mncjspdy">
                Gradients (optional)
              </span>
              <div className="space-y-4" data-id="element-5925" data-mp="3tpv4aks0ykep">
                <div data-id="element-5926" data-mp="3orjqjyylzpr7">
                  <label htmlFor="theme-config-primary-gradient" className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-5927" data-mp="3ot4lhpou5apl">
                    Primary gradient (optional)
                  </label>
                  <input id="theme-config-primary-gradient" type="text" value={primaryGradient} onChange={e => setPrimaryGradient(e.target.value)} placeholder="linear-gradient(135deg, #722F37 0%, #8F3D47 100%)" className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-5928" data-mp="labejvqkbk4e" />
                </div>
                <div data-id="element-5929" data-mp="143f9dkfio30l">
                  <label htmlFor="theme-config-secondary-gradient" className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-5930" data-mp="143xel6k5prkn">
                    Secondary gradient (optional)
                  </label>
                  <input id="theme-config-secondary-gradient" type="text" value={secondaryGradient} onChange={e => setSecondaryGradient(e.target.value)} placeholder="linear-gradient(135deg, #EFE7E1 0%, #F8F5F2 100%)" className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-5931" data-mp="13rz0dnt9uh7b" />
                </div>
              </div>
            </div>

            <CustomStylesheetSection bare surfaceLabel="customer portal" cssPlaceholder=".ocean-portal { ... }" data-id="element-5932" data-mp="1imd8gtbas677" />

            <div className="border-t border-[#E2E8F0] pt-5" data-id="element-5933" data-mp="1webeuwdmjnzo">
              <div className="flex flex-wrap items-center gap-3" data-id="element-5934" data-mp="1rf8a7d1k3js8">
                <Button variant="primary" onClick={() => onNotify('Theme saved')} data-id="element-5935" data-mp="1ri2tv2cqx0kl">
                  Save Theme
                </Button>
                <Button variant="secondary" onClick={handleReset} data-id="element-5936" data-mp="21epcrllihhjz">
                  Reset Theme
                </Button>
              </div>
              <p className="mt-2 text-[12px] text-[#64748B]" data-id="element-5937" data-mp="2l0hd0vopnesj">
                Reset discards any customised colours and reverts to the colours
                detected from your Shopify theme (or defaults if none are
                detected).
              </p>
            </div>
          </div>

          {/* Live preview */}
          {showPreview && <div className="border border-[#E2E8F0] rounded-lg overflow-hidden self-start w-full" data-id="element-5938" data-mp="21bytatmh4tl8">
            <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC] flex flex-wrap items-center gap-3" data-id="element-5939" data-mp="2626zb1cxy23s">
              <div className="flex bg-white rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-5940" data-mp="1sbtp0sckubba">
                {[{
                id: 'desktop' as const,
                icon: Monitor,
                label: 'Desktop'
              }, {
                id: 'mobile' as const,
                icon: Smartphone,
                label: 'Mobile'
              }].map(option => {
                const Icon = option.icon;
                return <button key={option.id} type="button" onClick={() => setDevice(option.id)} aria-pressed={device === option.id} aria-label={`${option.label} preview`} className={`p-1.5 rounded-md transition-colors ${device === option.id ? 'bg-[#F0F9FF] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-5941" data-mp="14844nwk97ojc">
                      <Icon className="w-4 h-4" data-id="element-5942" data-mp="pk9diklw9s8d" />
                    </button>;
              })}
              </div>

              <div className="flex items-center gap-2" data-id="element-5943" data-mp="1n43yze0pf463">
                <label htmlFor="theme-preview-zoom" className="text-[12px] font-medium text-[#475569]" data-id="element-5944" data-mp="1i8589jrwla2i">
                  Zoom
                </label>
                <select id="theme-preview-zoom" value={zoom} onChange={e => setZoom(Number(e.target.value))} className="h-8 pl-2 pr-6 border border-[#CBD5E1] rounded-md text-[12px] text-[#0F172A] bg-white focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5945" data-mp="1d9txn6tsavj8">
                  {zoomOptions.map(option => <option key={option} value={option} data-id="element-5946" data-mp="28he19kdhdvnl">
                      {option}%
                    </option>)}
                </select>
              </div>

              <div className="flex-1" data-id="element-5947" data-mp="2dggwkd3nsnid" />

              <button type="button" onClick={() => setInspectMode(mode => !mode)} aria-pressed={inspectMode} className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-[12px] font-medium transition-colors ${inspectMode ? 'border-[#0EA5E9] bg-[#F0F9FF] text-[#0EA5E9]' : 'border-[#CBD5E1] bg-white text-[#475569] hover:text-[#0F172A]'}`} data-id="element-5948" data-mp="2ifk01t3zqlo8">
                <Crosshair className="w-3.5 h-3.5" data-id="element-5949" data-mp="2w4vmex28pvhl" />
                Inspect mode
              </button>
            </div>

            <div className="p-4 border-b border-[#E2E8F0] space-y-3" data-id="element-5950" data-mp="313yu6p9luft9">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5951" data-mp="364cshflembe3">
                <div data-id="element-5952" data-mp="16sselslc17l0">
                  <label htmlFor="theme-preview-mode" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5953" data-mp="16tbmshl6khlu">
                    Portal mode
                  </label>
                  <select id="theme-preview-mode" value={previewMode} onChange={e => setPreviewMode(e.target.value as PortalPreviewMode)} className="w-full h-[40px] pl-3 pr-8 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] bg-white focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5954" data-mp="326w6s3ubtwtt">
                    {portalPreviewModes.map(item => <option key={item.id} value={item.id} data-id="element-5955" data-mp="2jhh6ibmgtbqh">
                        {item.label}
                      </option>)}
                  </select>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-5956" data-mp="2ejo47toa91vh">
                    Loyalty-only members see a different hero and tab set, so
                    check both when setting up a theme.
                  </p>
                </div>
                <div data-id="element-5957" data-mp="1ztgqtsd4u91n">
                  <label htmlFor="theme-preview-page" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5958" data-mp="1zsyqajjft8nx">
                    Portal page
                  </label>
                  {isLoyaltyOnly ? <select id="theme-preview-page" value={previewLoyaltyPage} onChange={e => setPreviewLoyaltyPage(e.target.value as PortalLoyaltyPageId)} className="w-full h-[40px] pl-3 pr-8 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] bg-white focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5959" data-mp="1lneu72jzztid">
                      {portalLoyaltyPages.map(item => <option key={item.id} value={item.id} data-id="element-5960" data-mp="24eg2ih70d1el">
                          {item.label}
                        </option>)}
                    </select> : <select id="theme-preview-page" value={previewPage} onChange={e => setPreviewPage(e.target.value as PortalPreviewPageId)} className="w-full h-[40px] pl-3 pr-8 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] bg-white focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5961" data-mp="29chkszwm0wp0">
                      {portalPreviewPages.map(item => <option key={item.id} value={item.id} data-id="element-5962" data-mp="nzy03ruwq1az">
                          {item.label}
                        </option>)}
                    </select>}
                </div>
                <div data-id="element-5963" data-mp="2qoo3q9s4ra0j">
                  <label htmlFor="theme-preview-modal" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5964" data-mp="2lo0cbmzzor5s">
                    Modal flow
                  </label>
                  <select id="theme-preview-modal" value={isLoyaltyOnly ? 'none' : previewModal} onChange={e => setPreviewModal(e.target.value as PortalPreviewModalId)} className="w-full h-[40px] pl-3 pr-8 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] bg-white focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5965" data-mp="2gup71qatsifz">
                    {(isLoyaltyOnly ? portalPreviewModals.filter(item => item.id === 'none') : portalPreviewModals).map(item => <option key={item.id} value={item.id} data-id="element-5966" data-mp="1t0nu1p0v35ia">
                        {item.label}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-5967" data-mp="3ne5jxioqoegh">
                  <label htmlFor="theme-preview-subscription" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5968" data-mp="3ndnd5s4gahub">
                    Subscription ID
                  </label>
                  <div className="flex gap-2" data-id="element-5969" data-mp="3p0xkig50v35">
                    <input id="theme-preview-subscription" type="text" value={subscriptionId} onChange={e => setSubscriptionId(e.target.value)} placeholder="e.g., SUB-2024-001" className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5970" data-mp="8h255r2d1xmu" />
                    <Button variant="secondary" onClick={handleLoadSubscription} data-id="element-5971" data-mp="3gxn92tg4y0j3">
                      Load
                    </Button>
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-5972" data-mp="3quru2py8vpyb">
                    The preview will render using that member's real data instead
                    of sample content.
                  </p>
                </div>
              </div>

              {inspectMode && <p className="text-[12px] text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-2.5 py-2 m-0" data-id="element-5973" data-mp="166x2hxwac8cv">
                  Switch inspect mode off to click through multi-step flows like
                  cancellation or swap.
                </p>}
            </div>

            <div className="p-4 bg-[#F1F5F9] overflow-auto" data-id="element-5974" data-mp="20zzb68xbax6u">
              <div className="origin-top-left" style={{
              transform: `scale(${zoom / 100})`,
              width: `${100 / (zoom / 100)}%`
            }} data-id="element-5975" data-mp="1w7oah3uqz7d8">
                <div className={device === 'mobile' ? 'max-w-[390px] mx-auto' : ''} data-id="element-5976" data-mp="25xrf61b8e9re">
                  <PortalThemePreview colors={colors} primaryGradient={isPausedState ? '' : primaryGradient} paused={isPausedState} mode={previewMode} page={previewPage} loyaltyPage={previewLoyaltyPage} modal={isLoyaltyOnly ? 'none' : previewModal} inspectMode={inspectMode} onInspect={handleInspect} loadedSubscriptionId={loadedSubscriptionId} data-id="element-5977" data-mp="25zu5kmgaq4wn" />
                </div>
              </div>
            </div>
          </div>}
        </div>
      </div>
    </Card>;
}